import { useFormik } from 'formik';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import * as Yup from 'yup';

import { useGetAccounts } from 'entities/cex-account';
import { useIsProfitableSpread } from 'entities/cex-order';
import { useLastPriceStore } from 'shared/store';
import { useTypedDispatch, useTypedSelector } from 'store';
import { dropAlertState, setAlertState } from 'store/slices/ui';
import { bn, divideBignumbers, humanizeBn, multiplyBignumbers } from 'tools';
import { ICexAccount } from 'types/accounts';
import { ICexOrderbookGridBotTask, ICexOrderbookGridBotTaskExtended } from 'types/bots';
import { convertTokenCEX } from 'utils/convertToken';
import { formatNumberToLocale } from 'utils/formats/formatNumberToLocale';
import { ECexName } from 'web3/cexs';

import { calcHalfSpreadPercentage } from './calcHalfSpreadPercentage';

interface IForm {
  startPriceMode: 'manual' | 'last_price' | 'mid_spread';
  account: ICexAccount | null;
  active: boolean;
  id: number;
  starting_price: string;
  spread_percentage: string;
  asks_order_number: string;
  bids_order_number: string;
  asks_min_amount: string;
  asks_max_amount: string;
  bids_min_amount: string;
  bids_max_amount: string;
  dynamic_mode_enabled: boolean;
  protection_enabled: boolean;
  price_delta: string;
  counter_orders_limit: string;
  dynamic_mode_offset: string;
  is_step_size_percentage: boolean;
  step_size: string;
  grid_refresh_mode_enabled: boolean;
  grid_refresh_min_delay: number;
  grid_refresh_max_delay: number;
  price_range_enabled: boolean;
  price_range_min_amount: string;
  price_range_max_amount: string;
  is_profitable_spread?: boolean | null;
}

const validationSchema = Yup.object({
  startPriceMode: Yup.string(),
  account: Yup.object().required('Field is required'),
  active: Yup.boolean(),
  id: Yup.number(),
  starting_price: Yup.number().when('startPriceMode', {
    is: 'start_price',
    then: schema => schema.required('Field is required').moreThan(0, 'Field is required'),
  }),
  spread_percentage: Yup.number()
    .required('Field is required')
    .moreThan(0, 'Should be more than 0')
    .test(
      'profitable_spread_spread_percentage',
      'Orderbook is unprofitable according to fees',
      function () {
        const is_profitable_spread = this.options.context?.is_profitable_spread;
        return is_profitable_spread !== false;
      },
    ),
  asks_order_number: Yup.number()
    .required('Field is required')
    .max(40, 'Should be less than or equal 40')
    .test(
      'asks_order_number_if_bids_zero',
      'When bids orders amount is zero, this field should be required',
      function (asks_order_number: number | string | undefined) {
        const bids_order_number = this.options.context?.bids_order_number;
        if (isNaN(bids_order_number) || Number(bids_order_number) === 0)
          return Number(asks_order_number) > 0;
        return true;
      },
    )
    .test(
      'asks_bids_match_if_price_range_enabled',
      'Asks and bids order numbers must be equal when price range is enabled',
      function (asks_order_number: number | undefined) {
        const { price_range_enabled, bids_order_number } = this.options.context ?? {};
        if (price_range_enabled) {
          return Number(asks_order_number) === Number(bids_order_number);
        }
        return true;
      },
    ),
  bids_order_number: Yup.number()
    .required('Field is required')
    .max(40, 'Should be less than or equal 40')
    .test(
      'bids_order_number_if_asks_zero',
      'When asks orders amount is zero, this field should be required',
      function (bids_order_number: number | string | undefined) {
        const asks_order_number = this.options.context?.asks_order_number;
        if (isNaN(asks_order_number) || Number(asks_order_number) === 0)
          return Number(bids_order_number) > 0;
        return true;
      },
    )
    .test(
      'bids_equal_asks_if_price_range_enabled',
      'Asks and bids order numbers must be equal when price range is enabled',
      function (bids_order_number: number | undefined) {
        const { price_range_enabled, asks_order_number } = this.options.context ?? {};
        if (price_range_enabled) {
          return Number(bids_order_number) === Number(asks_order_number);
        }
        return true;
      },
    ),

  asks_min_amount: Yup.number()
    .required('Field is required')
    .test('min_amount', 'Field is required', function (min_amount: number | string | undefined) {
      const asks_order_number = this.options.context?.asks_order_number;

      if (isNaN(asks_order_number) || Number(asks_order_number) === 0) return true;

      return bn(min_amount).gt(0);
    }),
  asks_max_amount: Yup.number()
    .required('Field is required')
    .test(
      'max_amount',
      'Should be more than min value',
      function (max_amount: number | string | undefined) {
        const min_amount = this.options.context?.asks_min_amount;
        const asks_order_number = this.options.context?.asks_order_number;

        if (isNaN(asks_order_number) || Number(asks_order_number) === 0) return true;

        if (isNaN(Number(min_amount)) || isNaN(Number(max_amount))) return true;

        return bn(max_amount).gte(bn(min_amount)) && bn(max_amount).gt(0);
      },
    ),
  bids_min_amount: Yup.number()
    .required('Field is required')
    .test('min_amount', 'Field is required', function (min_amount: number | string | undefined) {
      const bids_order_number = this.options.context?.bids_order_number;

      if (isNaN(bids_order_number) || Number(bids_order_number) === 0) return true;

      return bn(min_amount).gt(0);
    }),
  bids_max_amount: Yup.number()
    .required('Field is required')
    .test(
      'max_amount',
      'Should be more than min value',
      function (max_amount: number | string | undefined) {
        const min_amount = this.options.context?.bids_min_amount;
        const bids_order_number = this.options.context?.bids_order_number;

        if (isNaN(bids_order_number) || Number(bids_order_number) === 0) return true;

        if (isNaN(Number(min_amount)) || isNaN(Number(max_amount))) return true;

        return bn(max_amount).gte(bn(min_amount)) && bn(max_amount).gt(0);
      },
    ),
  is_step_size_percentage: Yup.boolean(),
  step_size: Yup.number()
    .required('Field is required')
    .moreThan(0, 'Should be more than 0')
    .test(
      'profitable_spread_step_size',
      'Orderbook is unprofitable according to fees',
      function () {
        const is_profitable_spread = this.options.context?.is_profitable_spread;
        return is_profitable_spread !== false;
      },
    )
    .test(
      'step_size',
      'Should be less than spread percentage',
      function (step_size: number | string | undefined) {
        const spread_percentage = this.options.context?.spread_percentage;
        const is_step_size_percentage = this.options.context?.is_step_size_percentage;

        if (isNaN(Number(spread_percentage)) || isNaN(Number(step_size))) return true;

        return is_step_size_percentage ? Number(spread_percentage) > Number(step_size) : true;
      },
    ),
  price_delta: Yup.number().test(
    'price_delta',
    'Field is required',
    function (price_delta: number | string | undefined) {
      const dynamic_mode_enabled = this.options.context?.dynamic_mode_enabled;

      return dynamic_mode_enabled ? Number(price_delta) > 0 : true;
    },
  ),
  counter_orders_limit: Yup.number().test(
    'counter_orders_limit',
    'Field is required',
    function (counter_orders_limit: number | string | undefined) {
      const dynamic_mode_enabled = this.options.context?.dynamic_mode_enabled;

      return dynamic_mode_enabled ? Number(counter_orders_limit) >= 0 : true;
    },
  ),
  dynamic_mode_offset: Yup.number().test(
    'dynamic_mode_offset',
    'Field is required',
    function (_dynamic_mode_offset: number | string | undefined) {
      const { dynamic_mode_enabled, dynamic_mode_offset } = this.options.context ?? {};

      // For dynamic mode, offset is required and must be non-negative.
      // When dynamic mode is disabled we don't block submit.
      if (!dynamic_mode_enabled) return true;

      if (
        dynamic_mode_offset === '' ||
        dynamic_mode_offset === undefined ||
        dynamic_mode_offset === null
      )
        return this.createError({ message: 'Field is required' });

      const numericOffset = Number(dynamic_mode_offset);

      if (Number.isNaN(numericOffset)) {
        return this.createError({ message: 'Field is required' });
      }

      if (numericOffset <= 0) {
        return this.createError({ message: 'Should be greater than 0' });
      }

      return true;
    },
  ),
  dynamic_mode_enabled: Yup.boolean(),
  protection_enabled: Yup.boolean(),
  price_range_min_amount: Yup.number().when('price_range_enabled', {
    is: true,
    then: schema =>
      schema
        .required('Field is required')
        .test(
          'min_amount',
          'Field is required',
          function (min_amount: number | string | undefined) {
            return bn(min_amount).gt(0);
          },
        ),
    otherwise: schema => schema.notRequired(),
  }),
  price_range_max_amount: Yup.number().when(['price_range_enabled', 'price_range_min_amount'], {
    is: (price_range_enabled: boolean, _price_range_min_amount: number) => price_range_enabled,
    then: schema =>
      schema
        .required('Field is required')
        .test(
          'max_amount',
          'Should be more than min value',
          function (max_amount: number | string | undefined) {
            const min_amount = this.parent.price_range_min_amount;

            if (isNaN(Number(min_amount)) || isNaN(Number(max_amount))) return true;

            return bn(max_amount).gte(bn(min_amount)) && bn(max_amount).gt(0);
          },
        ),
    otherwise: schema => schema.notRequired(),
  }),
});

interface IUseTaskModalProps {
  onSubmit: (task: ICexOrderbookGridBotTask) => void;
  task?: ICexOrderbookGridBotTaskExtended;
  id: number;
  mode: 'create' | 'edit';
}

export const useTaskModal = ({
  onSubmit: onSubmitProp,
  task,
  id,
  mode: _mode,
}: IUseTaskModalProps) => {
  const dispatch = useTypedDispatch();
  const cexPair = useTypedSelector(store => store.pairs.selectedCexPair)!;
  const isBitget = cexPair?.cex === ECexName.bitget;
  const lastStoredPrice = useLastPriceStore(s => s.getPriceOrFallback(cexPair?.id));

  const [isEdited, setIsEdited] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [isDynamicModeOffsetAuto, setIsDynamicModeOffsetAuto] = useState<boolean>(() => {
    const rawOffset = (task?.dynamic_mode as any)?.offset;
    const offsetString =
      rawOffset === undefined || rawOffset === null ? '' : String(rawOffset).trim();
    return offsetString === '';
  });
  const [dynamicModeOffsetSyncing, setDynamicModeOffsetSyncing] = useState<boolean>(false);

  const availableCurrencies = useMemo(
    () => [cexPair.token_base.symbol, cexPair.token_quote.symbol],
    [cexPair.token_base.symbol, cexPair.token_quote.symbol],
  );

  const [selectedAskCurrency, setSelectedAskCurrency] = useState(availableCurrencies[1]);
  const [selectedBidCurrency, setSelectedBidCurrency] = useState(availableCurrencies[1]);

  const { data: accountsResponse, isLoading: accountsLoading } = useGetAccounts(
    { pairId: cexPair?.id ?? 0, limit: 100 },
    { enabled: cexPair?.id != null },
  );
  const accounts = useMemo(
    () => accountsResponse?.data?.items ?? [],
    [accountsResponse?.data?.items],
  );

  // If backend provides offset, treat it as user-provided (no auto-sync by default).
  // If it's missing/empty, start in auto mode (offset follows spread until user edits it).
  useEffect(() => {
    const rawOffset = (task?.dynamic_mode as any)?.offset;
    const offsetString =
      rawOffset === undefined || rawOffset === null ? '' : String(rawOffset).trim();
    setIsDynamicModeOffsetAuto(offsetString === '');
  }, [task?.id, task?.dynamic_mode]);

  const initialValues = useMemo<IForm>(() => {
    if (task) {
      const isStepSizeInPercentage = Number(task.step_size) === 0 ? false : true;
      const rawOffset = (task.dynamic_mode as any)?.offset;
      const offsetString =
        rawOffset === undefined || rawOffset === null ? '' : String(rawOffset).trim();

      return {
        startPriceMode: task.starting_price_mode,
        account: accounts.find(el => el.id === task.account_id) ?? null,
        active: task.active,
        id,
        starting_price: task.starting_price,
        spread_percentage: task.spread_percentage,
        asks_order_number: task.ask_grid_config.order_number.toString(),
        bids_order_number: task.bid_grid_config.order_number.toString(),
        asks_min_amount: task.ask_grid_config.size_min,
        asks_max_amount: task.ask_grid_config.size_max,
        bids_min_amount: task.bid_grid_config.size_min,
        bids_max_amount: task.bid_grid_config.size_max,
        dynamic_mode_enabled: task.dynamic_mode.enabled,
        protection_enabled: task.protection_enabled,
        counter_orders_limit: task.dynamic_mode.counter_orders_limit.toString(),
        price_delta: task.dynamic_mode.price_delta,
        dynamic_mode_offset: offsetString,
        step_size: isStepSizeInPercentage ? task.step_size : task.step_size_ticks.toString(),
        is_step_size_percentage: isStepSizeInPercentage,
        grid_refresh_mode_enabled: task.grid_refresh.enabled,
        grid_refresh_min_delay: task.grid_refresh.min_delay,
        grid_refresh_max_delay: task.grid_refresh.max_delay,
        price_range_enabled: task.price_range.enabled,
        price_range_min_amount: task.price_range.min,
        price_range_max_amount: task.price_range.max,
        is_profitable_spread: null,
      };
    }

    return {
      startPriceMode: 'last_price',
      account: null,
      active: true,
      id,
      starting_price: '',
      spread_percentage: '',
      asks_order_number: '10',
      bids_order_number: '10',
      asks_min_amount: '',
      asks_max_amount: '',
      bids_min_amount: '',
      bids_max_amount: '',
      dynamic_mode_enabled: false,
      protection_enabled: true,
      counter_orders_limit: '',
      price_delta: '',
      dynamic_mode_offset: '',
      step_size: '',
      is_step_size_percentage: true,
      grid_refresh_mode_enabled: false,
      grid_refresh_min_delay: 0,
      grid_refresh_max_delay: 0,
      price_range_enabled: false,
      price_range_min_amount: '',
      price_range_max_amount: '',
      is_profitable_spread: null,
    };
  }, [id, task, accounts]);

  const onSubmit = useCallback(
    async ({
      startPriceMode,
      account,
      asks_min_amount,
      asks_max_amount,
      bids_min_amount,
      bids_max_amount,
      asks_order_number,
      bids_order_number,
      spread_percentage,
      starting_price,
      id,
      active,
      step_size,
      is_step_size_percentage,
      dynamic_mode_enabled,
      counter_orders_limit,
      price_delta,
      dynamic_mode_offset,
      grid_refresh_mode_enabled,
      grid_refresh_min_delay,
      grid_refresh_max_delay,
      price_range_enabled,
      price_range_min_amount,
      price_range_max_amount,
    }: IForm) => {
      if (!account) return;

      // Helper function to execute the actual submit logic
      const executeSubmit = async () => {
        try {
          setLoading(true);

          onSubmitProp({
            account_id: account.id,
            active,
            id,
            pair_id: cexPair.id,
            starting_price:
              startPriceMode === 'last_price' || startPriceMode === 'mid_spread'
                ? '0'
                : starting_price,
            starting_price_mode: startPriceMode,
            spread_percentage: spread_percentage,
            ask_grid_config: {
              order_number: Number(asks_order_number),
              size_min: humanizeBn(
                convertTokenCEX({
                  from: selectedAskCurrency,
                  to: cexPair.token_quote.symbol,
                  amount: asks_min_amount,
                  pair: cexPair,
                }),
                18,
              ),
              size_max: humanizeBn(
                convertTokenCEX({
                  from: selectedAskCurrency,
                  to: cexPair.token_quote.symbol,
                  amount: asks_max_amount,
                  pair: cexPair,
                }),
                18,
              ),
            },
            bid_grid_config: {
              order_number: Number(bids_order_number),
              size_min: humanizeBn(
                convertTokenCEX({
                  from: selectedBidCurrency,
                  to: cexPair.token_quote.symbol,
                  amount: bids_min_amount,
                  pair: cexPair,
                }),
                18,
              ),
              size_max: humanizeBn(
                convertTokenCEX({
                  from: selectedBidCurrency,
                  to: cexPair.token_quote.symbol,
                  amount: bids_max_amount,
                  pair: cexPair,
                }),
                18,
              ),
            },
            dynamic_mode: {
              counter_orders_limit: counter_orders_limit === '' ? 0 : Number(counter_orders_limit),
              enabled: dynamic_mode_enabled,
              offset: dynamic_mode_offset === '' ? '0' : dynamic_mode_offset,
              price_delta: price_delta === '' ? '0' : price_delta,
            },
            grid_refresh: {
              enabled: grid_refresh_mode_enabled,
              min_delay: grid_refresh_min_delay,
              max_delay: grid_refresh_max_delay,
            },
            price_range: {
              enabled: price_range_enabled,
              min: price_range_min_amount === '' ? '0' : price_range_min_amount,
              max: price_range_max_amount === '' ? '0' : price_range_max_amount,
            },
            protection_enabled: true,
            step_size: is_step_size_percentage ? step_size : '0',
            step_size_ticks: is_step_size_percentage ? 0 : Number(step_size),
          });
        } finally {
          setLoading(false);
        }
      };

      // Check if manual starting price mode is selected and calculate deviation
      if (startPriceMode === 'manual') {
        // Calculate deviation inline to avoid circular dependency
        let isRisky = false;
        let deviationValue = bn(0);

        if (
          lastStoredPrice.price &&
          lastStoredPrice.price !== '0' &&
          starting_price &&
          starting_price !== ''
        ) {
          try {
            const marketPrice = bn(lastStoredPrice.price);
            const startingPrice = bn(starting_price);

            if (marketPrice.gt(0) && startingPrice.gt(0)) {
              const absoluteDiff = startingPrice.sub(marketPrice).abs();
              const hundred = bn(100);
              const numerator = multiplyBignumbers([absoluteDiff, 18], [hundred, 18]);
              deviationValue = divideBignumbers([numerator, 18], [marketPrice, 18]);

              const fivePercent = bn(5);
              isRisky = deviationValue.gt(fivePercent);
            }
          } catch (error) {
            console.error('[onSubmit] Deviation calculation error:', error);
          }
        }

        if (isRisky) {
          const deviationPercent = formatNumberToLocale(humanizeBn(deviationValue, 18), 0);

          dispatch(
            setAlertState({
              type: 'sure',
              title: 'High Price Deviation Warning',
              text: React.createElement(
                'div',
                { style: { textAlign: 'center', lineHeight: '1.6' } },
                React.createElement(
                  'div',
                  { style: { marginBottom: '12px' } },
                  'The manual starting price differs from the current market price by',
                ),
                React.createElement(
                  'div',
                  { style: { fontSize: '1.25em', margin: '8px 0' } },
                  React.createElement(
                    'span',
                    { style: { color: '#ef4444', fontWeight: 'bold' } },
                    `${deviationPercent}%`,
                  ),
                ),
                React.createElement(
                  'div',
                  { style: { marginTop: '12px', marginBottom: '12px' } },
                  'This may result in unexpected behavior or loss of funds.',
                ),
                React.createElement(
                  'div',
                  { style: { marginTop: '16px', fontWeight: '500' } },
                  'Are you sure you want to continue?',
                ),
              ),
              onClose: () => {
                dispatch(dropAlertState());
              },
              onSubmit: async () => {
                dispatch(dropAlertState());
                await executeSubmit();
              },
            }),
          );
          return;
        }
      }

      // If no warning needed, execute submit directly
      await executeSubmit();
    },
    [
      onSubmitProp,
      cexPair,
      selectedAskCurrency,
      selectedBidCurrency,
      lastStoredPrice.price,
      dispatch,
    ],
  );

  const formik = useFormik({
    initialValues,
    onSubmit,
    validationSchema,
    enableReinitialize: true,
  });

  const profitableSpreadPayload = useMemo(() => {
    if (isBitget) return [];

    return [
      {
        accountId: formik.values.account?.id,
        spreadPercentage: formik.values.spread_percentage,
        stepSize: formik.values.step_size,
        isStepSizePercentage: formik.values.is_step_size_percentage,
      },
    ];
  }, [
    formik.values.account?.id,
    formik.values.spread_percentage,
    formik.values.step_size,
    formik.values.is_step_size_percentage,
    isBitget,
  ]);

  const { profitableSpreadMap, chosenAccountFees } = useIsProfitableSpread(profitableSpreadPayload);

  // Keep derived profitability flag in form values for schema validation
  useEffect(() => {
    if (isBitget || isNil(formik.values.account?.id) || isEmpty(profitableSpreadMap)) return;

    const entry = profitableSpreadMap[formik.values.account?.id];
    const isProf = entry?.isProfitable;
    if (formik.values.is_profitable_spread !== isProf) {
      formik
        .setFieldValue('is_profitable_spread', isProf)
        .then(async () => formik.setFieldTouched('spread_percentage', true))
        .then(async () => formik.setFieldTouched('step_size', true))
        .then(async () => formik.validateForm());
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profitableSpreadMap, formik.values.is_profitable_spread, isBitget]);

  const spreadProfitablePayload = useMemo(() => {
    if (isBitget || !formik.values.account?.id) {
      return { chosenAccountFees: null, isProfitableSpread: null, stepPercent: null };
    }

    return {
      chosenAccountFees: chosenAccountFees?.[formik.values.account.id],
      isProfitableSpread: profitableSpreadMap?.[formik.values.account.id]?.isProfitable ?? null,
      stepPercent: profitableSpreadMap?.[formik.values.account.id]?.stepPercent ?? null,
    };
  }, [formik.values.account?.id, chosenAccountFees, profitableSpreadMap, isBitget]);

  const _setFieldValue = useCallback(
    (field: keyof IForm, value: any) => {
      setIsEdited(true);

      return formik.setFieldValue(field, value);
    },
    [formik],
  );

  const canSyncDynamicModeOffsetFromSpread = useMemo(() => {
    const spread = formik.values.spread_percentage;
    return spread !== '' && !isNaN(Number(spread)) && Number(spread) > 0;
  }, [formik.values.spread_percentage]);

  const syncDynamicModeOffsetFromSpread = useCallback(async () => {
    if (!canSyncDynamicModeOffsetFromSpread) return;

    try {
      setDynamicModeOffsetSyncing(true);
      const newOffset = calcHalfSpreadPercentage(formik.values.spread_percentage);
      await _setFieldValue('dynamic_mode_offset', newOffset);
      setIsDynamicModeOffsetAuto(true);
    } finally {
      setDynamicModeOffsetSyncing(false);
    }
  }, [canSyncDynamicModeOffsetFromSpread, formik.values.spread_percentage, _setFieldValue]);

  const setDynamicModeOffsetFromUser = useCallback(
    (value: string) => {
      setIsDynamicModeOffsetAuto(false);
      _setFieldValue('dynamic_mode_offset', value);
    },
    [_setFieldValue],
  );

  // Auto-sync offset with spread while the field hasn't been edited manually (or was reset via sync button).
  useEffect(() => {
    if (!isDynamicModeOffsetAuto) return;
    if (!canSyncDynamicModeOffsetFromSpread) return;

    const newOffset = calcHalfSpreadPercentage(formik.values.spread_percentage);
    if (newOffset === formik.values.dynamic_mode_offset) return;

    _setFieldValue('dynamic_mode_offset', newOffset);
  }, [
    isDynamicModeOffsetAuto,
    canSyncDynamicModeOffsetFromSpread,
    formik.values.spread_percentage,
    formik.values.dynamic_mode_offset,
    _setFieldValue,
  ]);

  const startingPriceDeviation = useMemo(() => {
    // Return default object if no valid market price available
    if (!lastStoredPrice.price || lastStoredPrice.price === '0') {
      return { deviation: bn(0), risky: false };
    }

    // Return default object if no starting price entered
    if (!formik.values.starting_price || formik.values.starting_price === '') {
      return { deviation: bn(0), risky: false };
    }

    try {
      const marketPrice = bn(lastStoredPrice.price);
      const startingPrice = bn(formik.values.starting_price);

      // Return default object if either price is 0 or invalid
      if (marketPrice.lte(0) || startingPrice.lte(0)) {
        return { deviation: bn(0), risky: false };
      }

      const priceDiff = startingPrice.sub(marketPrice);

      // Calculate signed percentage deviation: ((startingPrice - marketPrice) / marketPrice) * 100
      const hundred = bn(100);
      const numerator = multiplyBignumbers([priceDiff, 18], [hundred, 18]);
      const percentageDeviation = divideBignumbers([numerator, 18], [marketPrice, 18]);

      // Check if deviation is risky (absolute value more than 5%)
      const fivePercent = bn(5);
      const risky = percentageDeviation.abs().gt(fivePercent);

      return { deviation: percentageDeviation, risky };
    } catch (error) {
      console.error('[startingPriceDeviation] Calculation error:', error);
      return { deviation: bn(0), risky: false };
    }
  }, [lastStoredPrice.price, formik.values.starting_price]);

  return {
    accounts,
    accountsLoading,
    isEdited,
    loading,
    handleSubmit: formik.handleSubmit,
    setFieldValue: _setFieldValue,
    values: formik.values,
    touched: formik.touched,
    errors: formik.errors,
    availableCurrencies,
    selectedAskCurrency,
    selectedBidCurrency,
    setSelectedAskCurrency,
    setSelectedBidCurrency,

    chosenAccountFees: spreadProfitablePayload.chosenAccountFees,
    isProfitableSpread: spreadProfitablePayload.isProfitableSpread,
    stepPercent: spreadProfitablePayload.stepPercent,
    startingPriceDeviation,
    canSyncDynamicModeOffsetFromSpread,
    dynamicModeOffsetSyncing,
    syncDynamicModeOffsetFromSpread,
    setDynamicModeOffsetFromUser,
    isBitget,
  };
};
